import { Trans, useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import cn from 'classnames';

import { selectCoursePricingOptionsData } from '../../../../store/features/courseOptions';
import { selectSiteHeaderData } from '../../../../../../store/features/siteHeader';
import { useRootSelector } from '../../../../../../store/hooks';
import { useIsComponentMounted } from '../../../../../../hooks';
import { useBasketCookie } from '../../../../hooks/basket/useBasketCookie';
import Alert from '../../../../shared/library/Alert';
import Link from '../../../../shared/library/Link';
import Tabs, { TabsProps } from '../../../../shared/library/Tabs';
import Tab from '../../../../shared/library/Tab';
import Typography from '../../../../shared/library/Typography';
import Tooltip from '../../../../shared/library/Tooltip';
import { getFormattedDates } from '../../../../helpers/dates';
import { renderHTMLString } from '../../../../../../helpers';
import { CoursePricingOption } from '../../../../store/features/courseOptions/types';

import { StyledInfoIcon } from './styles';
import { selectPageConfig } from '../../../../../../store/features/pageConfig';

export interface CourseOptionsTabsProps extends TabsProps {}

export default function CourseOptionsTabs(props: CourseOptionsTabsProps) {
  const isMounted = useIsComponentMounted();
  const { i18n, t } = useTranslation();
  const { dateFormat } = useRootSelector(selectSiteHeaderData);
  const { options } = useRootSelector(selectCoursePricingOptionsData);
  const { location } = useRootSelector(selectPageConfig);
  const siteOptionIndex = useWatch({
    name: 'siteOptionIndex',
  });
  const { removeOrderIdCookie, removeBasketIdCookie, basketCreated } =
    useBasketCookie();

  if (!options || options.length === 0) {
    return null;
  }

  const handleClearOrder = () => {
    removeBasketIdCookie();
    removeOrderIdCookie();
    window.location.reload();
  };

  const getLabel = (labelProps: CoursePricingOption) => {
    const { startDate, endDate, dateAsText } = labelProps;

    if (startDate) {
      return getFormattedDates({
        startDate,
        endDate,
        locale: i18n.language,
        dateFormat,
      });
    }

    return (
      dateAsText ||
      t('site.pricing.products-selection.select-option.on-demand.label')
    );
  };

  return (
    <div>
      <Tabs
        {...props}
        className={cn(props.className, 'informa-payment-course-options')}
      >
        {options.map((option) => {
          const isActiveOption = siteOptionIndex === option.index;
          const label = (
            <Typography
              variant="h5"
              neutralColorIndex={isActiveOption ? 800 : 600}
              component="span"
              className="informa-payment-course-options__date"
            >
              {getLabel(option)}
            </Typography>
          );
          const infoIcon =
            isMounted && option.productDescription ? (
              <Tooltip
                title={renderHTMLString(
                  option.productDescription,
                  location,
                  false,
                )}
              >
                <StyledInfoIcon fontSize="small" />
              </Tooltip>
            ) : undefined;

          return (
            <Tab
              value={option.index}
              label={label}
              icon={infoIcon}
              iconPosition="end"
              disabled={!option.enabled}
              key={option.index}
            />
          );
        })}
      </Tabs>

      {basketCreated && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography
            variant="smallText"
            className="informa-payment-course-options__clear-order-text"
          >
            <Trans
              i18nKey="site.pricing.products-selection.clear-order.label"
              components={[
                <Link onClick={handleClearOrder} sx={{ cursor: 'pointer' }} />,
              ]}
            />
          </Typography>
        </Alert>
      )}
    </div>
  );
}
